import {
  BLOG_LIST_DESCRIPTION,
  BlogPageShell,
} from "@/components/Blog/BlogPageShell";
import { blogPageHref } from "@/components/Blog/List";
import {
  BlogListProps,
  getBlogListProps,
  getEmptyBlogListProps,
} from "@/utils/blog";
import { absoluteUrl } from "@/utils/ssg";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

const BlogListPaginatedPage = ({ page }: BlogListProps) => {
  const title = `Blog — Page ${page} | Fllite`;
  const url = absoluteUrl(blogPageHref(page));

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={BLOG_LIST_DESCRIPTION} />
        {/* Self-referencing: a paginated listing is a distinct page linking to
            its own set of articles, not a duplicate of /blog. */}
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fllite" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={BLOG_LIST_DESCRIPTION} />
        <meta property="og:url" content={url} />
      </Head>
      <BlogPageShell page={page} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const { totalPages } = await getBlogListProps(1);

    return {
      // Page 1 lives at /blog; getStaticProps redirects /blog/page/1 there.
      paths: Array.from(
        { length: Math.max(totalPages - 1, 0) },
        (_, index) => ({
          params: { page: String(index + 2) },
        }),
      ),
      fallback: "blocking",
    };
  } catch (error) {
    // A flaky API at build time should not fail the build — pages still render
    // on demand through the blocking fallback.
    // eslint-disable-next-line no-console
    console.error("blog: failed to load page count for getStaticPaths", error);

    return { paths: [], fallback: "blocking" };
  }
};

export const getStaticProps: GetStaticProps<BlogListProps> = async ({
  params,
}) => {
  const page = Number(params?.page);

  if (!Number.isInteger(page) || page < 1) {
    return { notFound: true };
  }

  // Keep one URL for the first page rather than two listing the same articles.
  if (page === 1) {
    return { redirect: { destination: "/blog", permanent: true } };
  }

  let props: BlogListProps;

  try {
    props = await getBlogListProps(page);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`blog: could not prefetch page ${page}`, error);

    // Deliberately not a 404: an unreachable API says nothing about whether
    // this page exists, and Next would cache that answer.
    return { props: getEmptyBlogListProps(page), revalidate: 60 };
  }

  if (page > props.totalPages) {
    return { notFound: true, revalidate: 60 };
  }

  return { props, revalidate: 300 };
};

export default BlogListPaginatedPage;
